/**
 * Wallpaper System Types
 *
 * Defines wallpaper configuration for the Mac Studio theme's
 * rotating background system.
 */

export type WallpaperType = 'image' | 'gradient'

export interface WallpaperConfig {
  id: string
  name: string
  type: WallpaperType
  /** For image wallpapers - path relative to public/ */
  src?: string
  /** For gradient wallpapers - CSS gradient string (supports var(--scroll-hue)) */
  gradient?: string
  /** Whether this is a dark wallpaper (affects text contrast) */
  isDark?: boolean
  /** Attribution for the image */
  credit?: string
}

/**
 * Default wallpapers available in the system.
 * Users can add more by placing images in public/wallpapers/
 */
export const WALLPAPERS: WallpaperConfig[] = [
  {
    id: 'dynamic-gradient',
    name: 'Dynamic Gradient',
    type: 'gradient',
    gradient: `linear-gradient(
      135deg,
      hsl(calc(220 + var(--scroll-hue, 0)), 60%, 85%) 0%,
      hsl(calc(260 + var(--scroll-hue, 0)), 50%, 80%) 50%,
      hsl(calc(300 + var(--scroll-hue, 0)), 45%, 85%) 100%
    )`,
    isDark: false,
  },
  {
    id: 'gradient-sunset',
    name: 'Sunset',
    type: 'gradient',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
    isDark: false,
  },
  {
    id: 'gradient-ocean',
    name: 'Ocean',
    type: 'gradient',
    gradient: 'linear-gradient(135deg, #667eea 0%, #00d4ff 50%, #00ffd4 100%)',
    isDark: false,
  },
  {
    id: 'gradient-aurora',
    name: 'Aurora',
    type: 'gradient',
    gradient: 'linear-gradient(135deg, #0c1445 0%, #1a237e 30%, #4a148c 60%, #7b1fa2 100%)',
    isDark: true,
  },
  {
    id: 'sonoma-abstract',
    name: 'Sonoma Abstract',
    type: 'image',
    src: '/wallpapers/sonoma-abstract.webp',
    isDark: false,
    credit: 'Unsplash',
  },
  {
    id: 'mesh-gradient',
    name: 'Mesh Gradient',
    type: 'image',
    src: '/wallpapers/mesh-gradient.webp',
    isDark: false,
    credit: 'Generated',
  },
  // New wallpapers from Unsplash
  {
    id: 'gradient-flow',
    name: 'Gradient Flow',
    type: 'image',
    src: '/wallpapers/gradient-flow.webp',
    isDark: false,
    credit: 'Unsplash',
  },
  {
    id: 'purple-haze',
    name: 'Purple Haze',
    type: 'image',
    src: '/wallpapers/purple-haze.webp',
    isDark: true,
    credit: 'Unsplash',
  },
  {
    id: 'neon-waves',
    name: 'Neon Waves',
    type: 'image',
    src: '/wallpapers/neon-waves.webp',
    isDark: true,
    credit: 'Unsplash',
  },
  {
    id: 'sunset-beach',
    name: 'Sunset Beach',
    type: 'image',
    src: '/wallpapers/sunset-beach.webp',
    isDark: false,
    credit: 'Unsplash',
  },
  {
    id: 'mountain-peaks',
    name: 'Mountain Peaks',
    type: 'image',
    src: '/wallpapers/mountain-peaks.webp',
    isDark: false,
    credit: 'Unsplash',
  },
  {
    id: 'aurora-sky',
    name: 'Aurora Sky',
    type: 'image',
    src: '/wallpapers/aurora-sky.webp',
    isDark: true,
    credit: 'Unsplash',
  },
  {
    id: 'ocean-blue',
    name: 'Ocean Blue',
    type: 'image',
    src: '/wallpapers/ocean-blue.webp',
    isDark: false,
    credit: 'Unsplash',
  },
  {
    id: 'desert-dunes',
    name: 'Desert Dunes',
    type: 'image',
    src: '/wallpapers/desert-dunes.webp',
    isDark: false,
    credit: 'Unsplash',
  },
]

/**
 * Dark mode variants for gradient wallpapers
 */
export const WALLPAPERS_DARK: Record<string, string> = {
  'dynamic-gradient': `linear-gradient(
    135deg,
    hsl(calc(220 + var(--scroll-hue, 0)), 40%, 12%) 0%,
    hsl(calc(260 + var(--scroll-hue, 0)), 35%, 10%) 50%,
    hsl(calc(300 + var(--scroll-hue, 0)), 30%, 12%) 100%
  )`,
  'gradient-sunset': 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
  'gradient-ocean': 'linear-gradient(135deg, #0d1b2a 0%, #1b263b 50%, #415a77 100%)',
}

export const DEFAULT_WALLPAPER_ID = 'dynamic-gradient'
export const DEFAULT_ROTATION_INTERVAL = 5 * 60 * 1000 // 5 minutes
